import { useMemo, useRef } from "react";
import AdTokenTextarea, {
  type AdTokenTextareaHandle,
} from "@/modules/createEditor/components/AdTokenTextarea";
import AdTokenMessages from "@/shared/adTokens/AdTokenMessages";
import { useAdTokenCheck } from "@/shared/adTokens/useAdTokenCheck";
import type { AdToken } from "@/shared/adTokens/adTokens";
import { PERSON_TOKENS } from "./touchCopy";

/**
 * A touch's copy field, with the same ad token behaviour as an ad's copy field.
 *
 * The Email & SMS screens had grown their own token handling: a palette of ten chips
 * transcribed from the v10 prototype, a hand-rolled `insertAtCursor` that tracked the last
 * focused field in component state, and a bespoke "not a merge field" warning checked against
 * twelve strings compiled into the screen. None of it matched the vocabulary the server
 * resolves, and the insertion put the token wherever the last-focused field's caret had been
 * rather than where the writer was actually typing.
 *
 * The platform already had this built for ad copy (MPN-1930 to MPN-1933). Reusing it makes the
 * two editors behave identically, which is the point -- a writer moving between them should
 * not have to learn a second set of rules.
 *
 * The check is per field, as `AdCopyTokenNotes` does it, not over the touch's copy joined
 * together. That is what lets the fix be applied: `applyAliasFix` rewrites a span at an offset
 * into the text it was computed from, so it needs the field's own text and the field's own
 * handle. Run over a concatenation, the offsets belong to no field at all.
 *
 * `readOnly` renders a plain read-only textarea: there is nothing to insert into copy nobody
 * is editing, and no note to act on. It stays a real form control so its `<label>` still
 * associates, which a `<div>` does not.
 */
export default function CopyField({
  id,
  value,
  onValueChange,
  tokens,
  readOnly,
  className = "tx__field",
  singleLine = false,
  rows = 1,
  placeholder,
  "aria-invalid": ariaInvalid,
}: {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
  tokens: AdToken[];
  readOnly?: boolean;
  className?: string;
  singleLine?: boolean;
  rows?: number;
  placeholder?: string;
  "aria-invalid"?: boolean;
}) {
  const handle = useRef<AdTokenTextareaHandle | null>(null);
  const check = useAdTokenCheck(value, PERSON_TOKENS);
  // The picker offers what the check accepts, or `[` would hide the two commonest tokens
  // in the catalog while the note beside it called them fine.
  const vocabulary = useMemo(() => [...tokens, ...PERSON_TOKENS], [tokens]);

  if (readOnly) {
    return (
      <textarea
        id={id}
        className={className}
        readOnly
        rows={rows}
        value={value}
        placeholder={placeholder}
      />
    );
  }

  return (
    <>
      <AdTokenTextarea
        ref={(node) => {
          handle.current = node;
        }}
        id={id}
        className={className}
        tokens={vocabulary}
        value={value}
        onValueChange={onValueChange}
        rows={rows}
        singleLine={singleLine}
        placeholder={placeholder}
        aria-invalid={ariaInvalid}
      />
      <AdTokenMessages
        notes={check.notes}
        unavailable={check.unavailable}
        onUseCanonical={(note) =>
          handle.current?.edit((current) => check.applyAliasFix(note, current))
        }
      />
    </>
  );
}
